import type { Timesheet } from '@tabel/shared/schemas/timesheets';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CircleAlert, CircleCheck, Info, MessageCircleQuestion, Send, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { api } from '../lib/api';
import { ApiError, isTransient } from '../lib/errors';
import { hours, ruDate, shownHours } from '../lib/format';
import { dayLabel, nq, typeLabel, typeTone } from '../lib/questions';
import { errorText, useToast } from './ErrorToast';
import { Badge, Button, Callout, Modal } from './ui';

/*
 * Окно «Завершить» — сводка перед сдачей кадрам (003, US2, сценарии 8, 9; US3.6; FR-027, FR-037; R10): людей, часов,
 * правок, вопросов без ответа и сколько людей сейчас на объекте; до 5 вопросов без ответа; правило объекта
 * (Q5 `завершение_с_вопросами`). Кнопки: «Ответить сейчас»; «Завершить без ответов» — только если правило разрешает
 * сдачу с подтверждением; «Завершить и передать кадрам» — когда вопросов без ответа нет. Правило проверяет сервер
 * (S43): на гонку он отвечает `409 QUESTIONS_OPEN` — тост и новая сводка. Сводка не загрузилась (табель без функции
 * вопросов) — числа табеля на экране и обычное завершение.
 * «На объекте сейчас: N» — всегда, и при 0 (T115); сводки нет — «—». Сбой связи или сервера при «Завершить» — тост с
 * «Повторить» той же кнопки (T116, FR-042); отказ по правилу или статусу — только причина.
 */

const SCREEN = 'Timesheet';

export interface CompleteDialogProps {
  open: boolean;
  onClose: () => void;
  timesheetId: number;
  ts: Pick<
    Timesheet,
    'brigadeName' | 'periodStart' | 'periodEnd' | 'rows' | 'grandTotal' | 'grandTotalValue'
  >;
  /** «Ответить сейчас»: очередь вопросов без ответа. */
  onAnswerNow: () => void;
  /** Табель завершён: страница закрывает окно, показывает тост и перечитывает табель. */
  onCompleted: () => void;
}

function Stat({ label, value, tone }: { label: string; value: ReactNode; tone?: 'warn' | 'ok' }) {
  return (
    <div data-stat className="flex min-w-0 flex-col gap-1 bg-surface px-3.5 py-3">
      <dt className="text-[12.5px] text-ink-2">{label}</dt>
      <dd
        data-value
        className={`text-[20px] leading-[1.1] font-semibold tracking-[-0.02em] tabular-nums ${tone === 'warn' ? 'text-warn' : tone === 'ok' ? 'text-ok' : 'text-ink'}`}
      >
        {value}
      </dd>
    </div>
  );
}

export function CompleteDialog({
  open,
  onClose,
  timesheetId,
  ts,
  onAnswerNow,
  onCompleted,
}: CompleteDialogProps) {
  const { showError } = useToast();
  const summary = useQuery({
    queryKey: ['summary', timesheetId],
    queryFn: () => api.summary(timesheetId),
    enabled: open,
    staleTime: 0,
  });
  const complete = useMutation({
    mutationFn: (withOpenQuestions: boolean) => api.complete(timesheetId, withOpenQuestions),
    onSuccess: () => onCompleted(),
    onError: (e, withOpenQuestions) => {
      showError(e, {
        screen: SCREEN,
        action: 'complete',
        retry: isTransient(e) ? () => complete.mutate(withOpenQuestions) : undefined,
      });
      // Пока окно было открыто, появились вопросы или поменялось правило — показать новую сводку.
      if (e instanceof ApiError && e.code === 'QUESTIONS_OPEN') void summary.refetch();
    },
  });

  const s = summary.data;
  const failed = summary.isError;
  const open_ = s?.openQuestions.count ?? 0;
  const forbid = s?.completeWithOpen === 'запретить';
  const period = `«${ts.brigadeName}» за ${ruDate(ts.periodStart).slice(0, 5)} — ${ruDate(ts.periodEnd)}.`;
  const title =
    !s && !failed
      ? 'Завершить табель'
      : open_ > 0
        ? `Осталось ${nq(open_)} без ответа`
        : 'Завершить табель и передать кадрам?';
  const edits = ts.rows.reduce((n, r) => n + r.cells.filter((c) => c.manualHours != null).length, 0);

  const busy = complete.isPending;
  const cancel = (
    <Button disabled={busy} onClick={onClose}>
      Отмена
    </Button>
  );
  const finish = (
    <Button variant="primary" icon={Send} disabled={busy} onClick={() => complete.mutate(false)}>
      Завершить и передать кадрам
    </Button>
  );
  const answerNow = (
    <Button variant="primary" icon={MessageCircleQuestion} disabled={busy} onClick={onAnswerNow}>
      Ответить сейчас
    </Button>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="w-[560px]"
      title={title}
      description={`${period} После завершения табель правят только кадры, пока не вернут его вам.`}
    >
      <div className="flex flex-col gap-3">
        {!s && !failed ? (
          <p className="text-[13px] text-ink-3">Загрузка сводки…</p>
        ) : (
          <>
            {failed && (
              <Callout tone="bad" icon={CircleAlert}>
                Сводка не загрузилась: {errorText(summary.error, 'ошибка')}. Числа — из табеля на экране.
              </Callout>
            )}
            <dl className="grid grid-cols-4 gap-px overflow-hidden rounded-card border border-line bg-line max-sm:grid-cols-2">
              <Stat label="Людей" value={s ? s.people : ts.rows.length} />
              <Stat
                label="Часов"
                value={
                  s
                    ? shownHours(s.hoursValue, s.hours)
                    : shownHours(ts.grandTotalValue, ts.grandTotal) || hours(0)
                }
              />
              <Stat label="Правок ✎" value={s ? s.edits : edits} />
              <Stat
                label="Без ответа"
                value={s ? open_ : '—'}
                tone={s ? (open_ > 0 ? 'warn' : 'ok') : undefined}
              />
            </dl>
            {s && open_ > 0 && (
              <>
                <ul aria-label="Вопросы без ответа" className="flex flex-col gap-1">
                  {s.openQuestions.first.map((q) => (
                    <li
                      key={q.id}
                      className="flex items-center justify-between gap-2 rounded-control border border-line px-3 py-2 text-[13px]"
                    >
                      <span className="min-w-0 truncate">
                        {q.displayName} · {dayLabel(q.date)}
                      </span>
                      <Badge tone={typeTone(q.type)}>{typeLabel(q.type)}</Badge>
                    </li>
                  ))}
                </ul>
                {open_ > s.openQuestions.first.length && (
                  <p className="text-[13px] text-ink-3">и ещё {nq(open_ - s.openQuestions.first.length)}</p>
                )}
              </>
            )}
            {s && open_ === 0 && (
              <Callout tone="ok" icon={CircleCheck}>
                Все вопросы отвечены. Ответы стоят в ячейках и в журнале действий.
              </Callout>
            )}
            <Callout tone="neutral" icon={Info}>
              <span data-onsite>
                На объекте сейчас: <b className="font-semibold tabular-nums">{s ? s.onSiteToday : '—'}</b>.
                {s && s.onSiteToday > 0
                  ? ' Их часы за сегодня досчитаются после выхода, табель пересчитается до подписи.'
                  : ''}
              </span>
            </Callout>
            {s && open_ > 0 && forbid && (
              <Callout tone="bad" icon={CircleAlert}>
                Правила объекта: завершить табель можно только после ответа на все вопросы.
              </Callout>
            )}
            {s && open_ > 0 && !forbid && (
              <Callout tone="warn" icon={TriangleAlert}>
                Если завершить сейчас, кадры получат эти дни без ответа и, скорее всего, вернут табель.
              </Callout>
            )}
          </>
        )}
        <div
          data-actions
          className="flex flex-wrap items-center justify-end gap-2 pt-1 max-sm:[&>button]:flex-auto"
        >
          {!s && !failed ? (
            cancel
          ) : s && open_ > 0 ? (
            <>
              {!forbid && (
                <Button variant="ghost" disabled={busy} onClick={() => complete.mutate(true)}>
                  Завершить без ответов
                </Button>
              )}
              {!forbid && <span className="flex-1 max-sm:hidden" aria-hidden />}
              {cancel}
              {answerNow}
            </>
          ) : (
            <>
              {cancel}
              {finish}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
